/**
 * Utility functions for detecting and filtering browser extension errors
 */

export interface ExtensionErrorCheck {
  isExtensionError: boolean;
  reason: string;
}

/**
 * Check if an error comes from a browser extension
 */
export function isExtensionError(error: Error): ExtensionErrorCheck {
  const stack = error.stack || '';
  const message = error.message || '';

  // Check stack trace for extension URLs
  const extensionUrlPatterns = [
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    'ms-browser-extension://',
  ];

  for (const pattern of extensionUrlPatterns) {
    if (stack.includes(pattern)) {
      return {
        isExtensionError: true,
        reason: `Stack trace contains ${pattern}`,
      };
    }
  }

  // Check for common extension error messages
  const commonExtensionErrorPatterns = [
    'Non-Error promise rejection captured',
    'Script error.',
    'Object.handleError', // Common in extensions
    'chrome.runtime',
    'browser.runtime',
    // Add more patterns as you discover them
  ];

  for (const pattern of commonExtensionErrorPatterns) {
    if (message.includes(pattern) || stack.includes(pattern)) {
      return {
        isExtensionError: true,
        reason: `Contains extension error pattern: ${pattern}`,
      };
    }
  }

  return {
    isExtensionError: false,
    reason: 'No extension patterns detected',
  };
}

/**
 * Check if a URL is from a browser extension
 */
export function isExtensionUrl(url: string): boolean {
  const extensionUrlPatterns = [
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    'ms-browser-extension://',
  ];

  return extensionUrlPatterns.some((pattern) => url.includes(pattern));
}

/**
 * Check if a Sentry event should be filtered out due to browser extension errors or 401 authentication errors
 */
export function shouldFilterSentryEvent(event: {
  request?: { url?: string };
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      stacktrace?: {
        frames?: Array<{ filename?: string }>;
      };
    }>;
  };
  tags?: { status_code?: number };
}): { shouldFilter: boolean; reason?: string } {
  // Filter out AbortError - these are intentional request cancellations
  if (event.exception) {
    for (const exception of event.exception.values || []) {
      if (
        exception.type === 'AbortError' ||
        exception.value?.includes('signal is aborted')
      ) {
        return {
          shouldFilter: true,
          reason: 'AbortError - intentional request cancellation',
        };
      }
    }
  }

  // Filter out 401 authentication errors
  if (event.tags?.status_code === 401) {
    return {
      shouldFilter: true,
      reason: '401 authentication error filtered out',
    };
  }

  // Filter out errors from extension URLs in event.request
  if (event.request?.url && isExtensionUrl(event.request.url)) {
    return {
      shouldFilter: true,
      reason: `Extension URL detected: ${event.request.url}`,
    };
  }

  // Check exception frames for extension URLs and PostHog replay errors
  if (event.exception) {
    for (const exception of event.exception.values || []) {
      const stacktrace = exception.stacktrace;
      if (stacktrace?.frames) {
        const hasExtensionFrame = stacktrace.frames.some(
          (frame: { filename?: string }) => {
            const filename = frame.filename || '';
            return isExtensionUrl(filename);
          },
        );

        if (hasExtensionFrame) {
          return {
            shouldFilter: true,
            reason: 'Stack trace contains extension frame',
          };
        }

        const hasPostHogReplayFrame = stacktrace.frames.some(
          (frame: { filename?: string }) => {
            const filename = frame.filename || '';
            return (
              filename.includes('posthog-recorder.js') ||
              filename.includes('posthog-replay')
            );
          },
        );

        if (hasPostHogReplayFrame) {
          return {
            shouldFilter: true,
            reason: 'PostHog replay error',
          };
        }
      }

      // Check error message against common extension patterns
      const errorMessage = exception.value || '';
      if (errorMessage) {
        const mockError = new Error(errorMessage);
        const extensionCheck = isExtensionError(mockError);
        if (extensionCheck.isExtensionError) {
          return {
            shouldFilter: true,
            reason: extensionCheck.reason,
          };
        }
      }
    }
  }

  return { shouldFilter: false };
}

/**
 * Filter function for promise rejection handlers
 */
export function shouldIgnorePromiseRejection(reason: unknown): boolean {
  // Filter out AbortError from promise rejections
  if (reason instanceof Error && reason.name === 'AbortError') {
    return true;
  }

  if (
    typeof reason === 'object' &&
    reason !== null &&
    'name' in reason &&
    reason.name === 'AbortError'
  ) {
    return true;
  }

  if (reason instanceof Error) {
    return isExtensionError(reason).isExtensionError;
  }

  if (typeof reason === 'string') {
    // Create a mock error to check patterns
    const mockError = new Error(reason);
    return isExtensionError(mockError).isExtensionError;
  }

  return false;
}
